import React, { memo, useCallback } from 'react';
import { Box, Paper, TextField, Typography } from '@mui/material';
import styled from '@mui/system/styled';
import { DateString } from 'routes/HumanResourcesModule/business/types';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import HistoricDataGrid from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/components/ValueByDate/HistoricDataGrid';
import PreviousSimulationDataGrid from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/components/ValueByDate/PreviousSimulationDataGrid';
import {
    HistoricDataFilters,
    PreviousDataFilters,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/components/ValueByDate/helpers/dataHelpers';
import { getScopedT } from 'helpers/translationHelpers';
import { FormState } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/AddHypotheses/AgentHypothesisModal/AgentHypothesisModal';
import _ from 'lodash';
import {
    getHrPreviousSimulationApiInfo,
    getHrSimulationHistoricApiInfo,
} from './helpers/valueByDateHelpers';
import {
    AgentPayComponentHypothesis,
    DetailedAgentPayComponentHypothesis,
} from './helpers/types';
import { useTranslation } from 'react-i18next';

const StyModalSection = styled(Paper, { name: 'StyModalSection' })({
    padding: '1rem',
    '& + &': {
        marginTop: '1.5rem',
    },
});

const TRANSLATION_KEY =
    'humanResources.simulation.agentHypothesis.creationModal';

type Props = {
    hrSimulation: HrSimulation;
    onValuesByDatesChange: (
        newValuesByDates: Record<DateString, number>,
    ) => void;
    filters: HistoricDataFilters | PreviousDataFilters;
    formState?: FormState; // in edit mode the user should not be able to edit data
    setFormState?: React.Dispatch<React.SetStateAction<FormState>>;
    hypothesis?:
        | AgentPayComponentHypothesis
        | DetailedAgentPayComponentHypothesis;
    isUpdateEnabled?: boolean;
};

/**
 * Generates the content for the Agent Pay Component Hypothesis Modal.
 * This component is used in edit mode and also in view mode, with non-editable fields
 *
 * @param {Props} filters - The filters for the modal.
 * @param {Props} hrSimulation - The HR simulation data.
 * @param {Props} formState - The form state data. Can be undefined if in view mode.
 * @param {Props} setFormState - Function to set the form state.Can be undefined if in view mode.
 * @param {Props} hypothesis - The hypothesis data.
 * @param {Props} onValuesByDatesChange - Function to handle values by dates change.
 * @param {boolean} [isUpdateEnabled=true] - Flag to enable/disable update.
 * @return {React.ReactElement} The content for the Agent Pay Component Hypothesis Modal.
 */
const AgentPayComponentHypothesisModalContent = ({
    filters,
    hrSimulation,
    formState,
    setFormState,
    hypothesis,
    onValuesByDatesChange,
    isUpdateEnabled = true,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const tScoped = getScopedT(TRANSLATION_KEY, t);
    const { apiCacheKey: historicApiKey, endpoint: historicEndpoint } =
        getHrSimulationHistoricApiInfo(hrSimulation.id);

    const {
        apiCacheKey: previousSimulationApiKey,
        endpoint: previousSimulationEndpoint,
    } = getHrPreviousSimulationApiInfo(hrSimulation.id);
    const onChange = useCallback(
        (value: boolean) => {
            if (setFormState !== undefined) {
                setFormState((prevState) => ({
                    ...prevState,
                    hasPonctualPrimesChanges: value,
                }));
            }
        },
        [setFormState],
    );
    return (
        <Box>
            <StyModalSection elevation={0}>
                <HistoricDataGrid
                    hrSimulation={hrSimulation}
                    filters={filters}
                    apiCacheKey={historicApiKey}
                    endpoint={historicEndpoint}
                    translationKey={TRANSLATION_KEY}
                />
            </StyModalSection>
            <StyModalSection elevation={0}>
                <PreviousSimulationDataGrid
                    hrSimulation={hrSimulation}
                    filters={filters}
                    hypothesis={hypothesis}
                    onValuesByDatesChange={onValuesByDatesChange}
                    setHasChanges={onChange}
                    isUpdateEnabled={isUpdateEnabled}
                    apiCacheKey={previousSimulationApiKey}
                    endpoint={previousSimulationEndpoint}
                    translationKey={TRANSLATION_KEY}
                />
            </StyModalSection>
            <StyModalSection elevation={0}>
                <Typography variant="h4">{tScoped('comment')}</Typography>
                <TextField
                    fullWidth
                    multiline
                    disabled={!isUpdateEnabled}
                    value={formState ? formState.comment : hypothesis?.comment}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (setFormState && formState) {
                            setFormState({
                                ...formState,
                                comment: e.target.value,
                            });
                        }
                    }}
                />
            </StyModalSection>
        </Box>
    );
};

export default memo(AgentPayComponentHypothesisModalContent, _.isEqual);
